import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

/**
 * CModal - the one shell every popup in the app renders through.
 *
 * It owns everything that makes a popup modal, so a page only supplies the
 * panel's look and content:
 *
 * - Portals to document.body. Mounted inline, the fixed overlay was a child of
 *   the page root's `space-y-*`, whose `> * + *` rule gave it margin-top and
 *   left an undimmed strip across the top of the viewport. A portal also takes
 *   the popup out of the page's stacking context.
 * - Moves focus into the panel on open (`initialFocus`, else the first Tab
 *   stop, else the panel itself) and back to whatever had it on close. When
 *   that element is gone (a deleted row), focus goes to `returnFocusTo`, else
 *   to the page's <main>.
 * - Keeps Tab and Shift+Tab inside the panel, and closes on Escape and on a
 *   backdrop click. `dismissible={false}` turns both closes off (a request in
 *   flight); `closeOnBackdrop={false}` turns off only the click, for a popup a
 *   stray click would throw work away from.
 * - Locks page scroll while open.
 *
 * The keyboard is handled on `document` for the topmost popup, not on the
 * panel: a submit button that disables itself mid-request, or a control that
 * unmounts, drops focus to <body>, and a panel-level handler would then never
 * see Escape or Tab again. For the same reason the panel takes focus back when
 * it loses it to nowhere.
 *
 * The caller labels the dialog by passing its title's id as `labelledBy`.
 * Any other prop (data-testid, aria-describedby) lands on the panel.
 */

const FOCUSABLE = [
  'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', 'iframe', '[contenteditable="true"]',
  '[tabindex]',
].join(',');

// checkVisibility skips a display:none control (a `hidden` file input) that
// would otherwise be picked as the first or last stop. Browsers without it,
// and jsdom, treat everything as visible.
const isVisible = (el) => (typeof el.checkVisibility === 'function' ? el.checkVisibility() : true);

// tabIndex >= 0: a control taken out of the Tab order (PasswordInput's eye
// toggle) is never where Tab lands, so it cannot be the first or last stop.
const tabStopsIn = (root) => Array.from(root.querySelectorAll(FOCUSABLE))
  .filter((el) => el.tabIndex >= 0 && isVisible(el));

// A trigger that starts a request usually disables itself, and a disabled
// button drops focus to <body>. A popup that opens when the request settles
// (upload results, an export error) would then have nothing to hand focus back
// to, so remember the last element that really held focus.
let lastFocused = null;
if (typeof document !== 'undefined') {
  document.addEventListener('focusin', (event) => { lastFocused = event.target; }, true);
}

// Open panels, oldest first. Only the last answers the keyboard.
const openPanels = [];
const isTopmost = (panel) => openPanels[openPanels.length - 1] === panel;

// The control a panel took focus back from, so a popup stacked on the panel
// can hand focus to that control, rather than the panel, when it closes.
const rescuedFrom = new WeakMap();

const focusIfPossible = (el, options) => {
  if (!el || !el.isConnected || el === document.body) return false;
  el.focus(options);
  return document.activeElement === el;
};

// The control focus went back to can still be removed afterwards: the row a
// delete confirm deleted goes when the list refetch lands, which can be long
// after the popup closed, and Approve goes once the leave reloads as decided.
// Focus would then drop to <body>. So watch it until focus moves on or it goes.
// ponytail: a subtree observer on <body> lives until the next focus change; it
// only checks isConnected per mutation batch, so it is cheap while it waits.
const watchUntilGone = (el, fallback) => {
  let observer = null;
  const stop = () => {
    observer.disconnect();
    document.removeEventListener('focusin', stop, true);
  };
  observer = new MutationObserver(() => {
    if (el.isConnected) return;
    stop();
    const active = document.activeElement;
    if (openPanels.length === 0 && (!active || active === document.body)) fallback();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('focusin', stop, true);
};

const restoreFocus = (opener, returnFocusTo) => {
  // Layout's <main> is focusable (tabIndex -1) for exactly this.
  const fallback = () => focusIfPossible(returnFocusTo && returnFocusTo.current)
    || focusIfPossible(document.querySelector('main'), { preventScroll: true });
  const rescued = opener && rescuedFrom.get(opener);
  if (rescued) rescuedFrom.delete(opener);
  if (focusIfPossible(rescued)) watchUntilGone(rescued, fallback);
  else if (focusIfPossible(opener)) watchUntilGone(opener, fallback);
  else fallback();
};

const trapTab = (event, panel) => {
  const stops = tabStopsIn(panel);
  if (stops.length === 0) {
    event.preventDefault();
    panel.focus();
    return;
  }
  const first = stops[0];
  const last = stops[stops.length - 1];
  const active = document.activeElement;
  let next = null;
  if (active === panel || !panel.contains(active)) next = event.shiftKey ? last : first;
  else if (event.shiftKey && active === first) next = last;
  else if (!event.shiftKey && active === last) next = first;
  if (next) {
    event.preventDefault();
    next.focus();
  }
};

// One lock for however many popups are open, so closing one of two stacked
// popups does not unlock the page under the other.
let scrollLocks = 0;
let savedBodyStyle = null;

const lockScroll = () => {
  scrollLocks += 1;
  if (scrollLocks > 1) return;
  const { body, documentElement } = document;
  savedBodyStyle = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
  // Hiding a classic (non-overlay) scrollbar widens the page; pad by its width
  // so nothing behind the backdrop shifts sideways.
  const scrollbar = window.innerWidth - documentElement.clientWidth;
  body.style.overflow = 'hidden';
  if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
};

// removeProperty for an unset value: jsdom ignores `style.paddingRight = ''`.
const restoreStyle = (style, prop, value) => (value ? style.setProperty(prop, value) : style.removeProperty(prop));

const unlockScroll = () => {
  scrollLocks -= 1;
  if (scrollLocks > 0) return;
  restoreStyle(document.body.style, 'overflow', savedBodyStyle.overflow);
  restoreStyle(document.body.style, 'padding-right', savedBodyStyle.paddingRight);
};

const CModal = ({
  onClose,
  labelledBy,
  dismissible = true,
  closeOnBackdrop = true,
  initialFocus,
  returnFocusTo,
  className,
  overlayClassName,
  children,
  ...panelProps
}) => {
  const panelRef = useRef(null);
  const pressedBackdrop = useRef(false);
  // The document listeners below are added once, so they read the current
  // props through this ref rather than the ones from the first render.
  const latest = useRef(null);
  useLayoutEffect(() => {
    latest.current = { onClose, dismissible, initialFocus, returnFocusTo };
  });

  useLayoutEffect(() => {
    // Captured here rather than during render: when one popup hands over to
    // another in the same commit, the closing one has already put focus back
    // on the page, which is where this one should return it.
    const opener = document.activeElement === document.body ? lastFocused : document.activeElement;
    const panel = panelRef.current;
    openPanels.push(panel);
    if (!panel.contains(document.activeElement)) {
      const named = latest.current.initialFocus && latest.current.initialFocus.current;
      (named || tabStopsIn(panel)[0] || panel).focus();
    }
    lockScroll();

    // Chrome fires focusout when the focused control is disabled or removed,
    // and focus lands on <body>. Once the browser has finished moving it, take
    // it back. Focus that went somewhere real (a popup stacked on this one, or
    // another window) is left alone.
    let rescue = null;
    const onFocusOut = (event) => {
      const lost = event.target;
      clearTimeout(rescue);
      rescue = setTimeout(() => {
        const active = document.activeElement;
        if (!isTopmost(panel) || (active && active !== document.body)) return;
        rescuedFrom.set(panel, lost);
        panel.focus();
      });
    };

    // Bubble phase, so a control inside that claims a key first (an open
    // Dropdown's Escape) has already called preventDefault.
    const onKeyDown = (event) => {
      if (!isTopmost(panel) || event.defaultPrevented) return;
      if (event.key === 'Tab') {
        trapTab(event, panel);
        return;
      }
      if (event.key !== 'Escape') return;
      // The Escape that cancels an IME composition belongs to the input.
      // Safari reports it as keyCode 229 rather than isComposing.
      if (event.isComposing || event.keyCode === 229 || !latest.current.dismissible) return;
      event.preventDefault();
      latest.current.onClose();
    };

    panel.addEventListener('focusout', onFocusOut);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      clearTimeout(rescue);
      panel.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('keydown', onKeyDown);
      openPanels.splice(openPanels.indexOf(panel), 1);
      unlockScroll();
      restoreFocus(opener, latest.current.returnFocusTo);
    };
  }, []);

  // Only a press that starts AND ends on the backdrop closes it, so a text
  // selection dragged out of the panel does not. Preventing the mousedown also
  // stops focus leaving the panel for <body> when the click is ignored.
  const handleMouseDown = (event) => {
    pressedBackdrop.current = event.target === event.currentTarget;
    if (pressedBackdrop.current) event.preventDefault();
  };

  const handleClick = (event) => {
    const onBackdrop = pressedBackdrop.current && event.target === event.currentTarget;
    pressedBackdrop.current = false;
    if (onBackdrop && dismissible && closeOnBackdrop) onClose();
  };

  return createPortal(
    // The backdrop is a pointer target only; keyboard users close with Escape.
    <div
      className={twMerge('fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4', overlayClassName)}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      data-testid="c-modal-backdrop"
    >
      <div
        {...panelProps}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={twMerge('outline-none', className)}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

export default CModal;
